import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from "../api/axios";

const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
const RESET_DELAY_MS = 5000;

const Call = () => {
  const [isPending, setIsPending] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const attemptRef = useRef(0);
  const abortControllerRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const resetTimeoutRef = useRef(null);

  const clearPending = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  const attemptCall = useCallback(async () => {
    attemptRef.current += 1;
    setIsPending(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const attemptIndex = attemptRef.current;
    const retryLabel = attemptIndex > 1 ? ` (reîncercare ${attemptIndex}/${MAX_ATTEMPTS})` : '';
    setStatus({ state: 'progress', message: `Se deschide...${retryLabel}` });

    try {
      const { data } = await axios.post('/calls', undefined, { signal: controller.signal });
      abortControllerRef.current = null;

      const backendStatus = (data?.status || '').toString().toLowerCase();
      if (['queued', 'ringing', 'in-progress', 'completed', 'success'].includes(backendStatus)) {
        setStatus({ state: 'success', message: data?.message || 'Poarta se deschide.' });
        resetTimeoutRef.current = setTimeout(() => {
          attemptRef.current = 0;
          setIsPending(false);
          setStatus({ state: 'idle', message: '' });
        }, RESET_DELAY_MS);
        return;
      }

      throw new Error(data?.message || 'Call failed');
    } catch (error) {
      if (controller.signal.aborted || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }

      abortControllerRef.current = null;

      if (attemptRef.current < MAX_ATTEMPTS) {
        retryTimeoutRef.current = setTimeout(() => {
          attemptCall();
        }, RETRY_DELAY_MS);
        return;
      }

      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        error?.message ||
        'Nu am putut deschide poarta. Te rugăm să încerci din nou.';

      attemptRef.current = 0;
      setIsPending(false);
      setStatus({
        state: 'error',
        message: apiMessage,
      });
    }
  }, []);

  const makeCall = useCallback(() => {
    if (isPending) {
      return;
    }

    clearPending();
    setStatus({ state: 'idle', message: '' });
    attemptRef.current = 0;
    attemptCall();
  }, [attemptCall, clearPending, isPending]);

  useEffect(() => {
    return () => {
      clearPending();
    };
  }, [clearPending]);

  return (
    <>
      <button className='openGate' disabled={isPending} onClick={makeCall}>
        {isPending ? 'Se deschide...' : 'Deschide Poarta'}
      </button>
      {status.message && (
        <p className={`call_status ${status.state}`} aria-live="polite">
          {status.message}
        </p>
      )}
    </>
  );
};

export default Call;
